import { useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useTestKit, useDeleteTestKit } from "@/hooks/use-test-kits";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { formatDateOnly } from "@/lib/dates";
import { usePermissions } from "@/hooks/use-permissions";
import { useAppStore } from "@/store";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { KitStatusBadge } from "@/components/test-kits/kit-status-badge";
import { KitCalibrationExpiryBadge } from "@/components/test-kits/kit-calibration-expiry-badge";
import { CalibrationTimeline } from "@/components/test-kits/calibration-timeline";
import { AddCalibrationDialog } from "@/components/test-kits/add-calibration-dialog";
import { isKitOrgOwned } from "@/components/test-kits/kit-ownership";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Card,
  EmptyValue,
  EmptyState,
  PageSkeleton,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_authenticated/test-kits/$id/")({
  component: TestKitDetailPage,
});

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted">{label}</dt>
      <dd className="text-sm text-on-surface">{children}</dd>
    </div>
  );
}

function TestKitDetailPage() {
  const { t } = useTranslation("test-kits");
  const navigate = useNavigate();
  const toasts = useToast();
  const { id } = Route.useParams();
  const { can } = usePermissions();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: kit, isLoading, isError } = useTestKit(id);
  useRecentPageTitle(kit?.serial_number ?? undefined);
  const deleteMutation = useDeleteTestKit(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [calibrationOpen, setCalibrationOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !kit) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[{ label: t("list.title"), to: "/testers", search: { tab: "kits" } }]}
        />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  const archived = !!kit.deleted_at;
  // Calibration records and kit documents follow kit VISIBILITY, not
  // ownership: the RLS write arms admit them for any kit the org can see
  // (org-owned, company-owned-under-org, a non-revoked tester's), so the
  // update grant + non-archived is the whole gate — a refusal surfaces as
  // the API's typed error, never as UI pre-filtering.
  const canAddCalibration = can("test_kits", "update") && !archived;
  // Identity writes (PATCH, DELETE) are RLS-refused on tester-owned kits;
  // company-under-org kits are RLS-admitted for identity writes, and the UI
  // is deliberately stricter there — those affordances stay org-owned-only.
  const orgOwned = isKitOrgOwned(kit, orgId);
  const canEdit = orgOwned && can("test_kits", "update") && !archived;
  const canDelete = orgOwned && can("test_kits", "delete") && !archived;
  const latest = kit.latest_calibration;

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        toasts.success("test-kits:detail.deletedToast");
        setConfirmOpen(false);
        void navigate({ to: "/testers", search: { tab: "kits" } });
      },
      onError: (err) => {
        setConfirmOpen(false);
        if (err instanceof ApiError && err.code === "kit_record_immutable_post_soft_delete") {
          toasts.error("test-kits:errors.softDeleted");
        } else {
          toasts.error("test-kits:errors.updateFailed");
        }
      },
    });
  };

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("list.title"), to: "/testers", search: { tab: "kits" } },
          { label: kit.serial_number },
        ]}
      />
      <PageHeader
        title={kit.serial_number}
        meta={<KitStatusBadge status={kit.status} />}
        actions={
          canEdit || canDelete ? (
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button
                  onClick={() => void navigate({ to: "/test-kits/$id/edit", params: { id } })}
                >
                  {t("detail.editCta")}
                </Button>
              )}
              {canDelete && (
                <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                  {t("detail.deleteCta")}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <Card title={t("detail.identityTitle")}>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Row label={t("columns.manufacturer")}>{kit.manufacturer}</Row>
              <Row label={t("columns.model")}>{kit.model ?? <EmptyValue />}</Row>
              <Row label={t("columns.serialNumber")}>{kit.serial_number}</Row>
              <Row label={t("columns.kitType")}>{kit.kit_type ?? <EmptyValue />}</Row>
              <Row label={t("columns.owner")}>
                <span className="flex flex-col gap-0.5">
                  <span>{kit.owner_label?.trim() ? kit.owner_label : <EmptyValue />}</span>
                  <span className="text-xs text-muted">{t(`ownerKind.${kit.owner_kind}`)}</span>
                </span>
              </Row>
              <Row label={t("columns.status")}>
                <KitStatusBadge status={kit.status} />
              </Row>
              <Row label={t("detail.latestCalibration")}>
                {latest?.calibration_expires_at ? (
                  <span className="inline-flex items-center gap-2">
                    {formatDateOnly(latest.calibration_expires_at)}
                    <KitCalibrationExpiryBadge expiresAt={latest.calibration_expires_at} />
                  </span>
                ) : (
                  <span className="text-muted">{t("detail.neverCalibrated")}</span>
                )}
              </Row>
              <Row label={t("form.notes")}>{kit.notes ?? <EmptyValue />}</Row>
            </dl>
          </Card>

          <Card>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-on-surface">
                {t("detail.calibrationsTitle")}
              </h3>
              {canAddCalibration && (
                <Button size="sm" onClick={() => setCalibrationOpen(true)}>
                  {t("detail.addCalibrationCta")}
                </Button>
              )}
            </div>
            <CalibrationTimeline kitId={id} />
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-5">
          <Card title={t("detail.testsPerformedTitle")}>
            <EmptyState variant="inline" title={t("detail.testsPerformedEmpty")} />
          </Card>
          {/* Documents ride the same visibility gate as calibration records. */}
          {orgId && canAddCalibration && (
            <div>
              <EntityDocuments entityType="test_kit" entityId={id} orgId={orgId} />
              <p className="mt-2 text-xs text-muted">
                {t("field-credentials:submittedEvidence.documentsProvenance")}
              </p>
            </div>
          )}
        </div>
      </div>

      {canDelete && (
        <ConfirmDialog
          open={confirmOpen}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleDeleteConfirm}
          confirmDisabled={deleteMutation.isPending}
          title={t("detail.deleteTitle")}
          message={t("detail.deleteBody", { serial: kit.serial_number })}
          confirmLabel={t("detail.deleteConfirm")}
          cancelLabel={t("detail.deleteCancel")}
          variant="danger"
        />
      )}

      <AddCalibrationDialog
        kitId={id}
        open={calibrationOpen}
        onClose={() => setCalibrationOpen(false)}
      />
    </div>
  );
}
